import React, { useState } from 'react';
import Button from './Button';
import { EMPTY_ENQUIRY_FORM } from '../config/ifysForms';
import { submitIfysEnquiry } from '../utils/submitIfysEnquiry';

const inputBase =
  'w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm sm:text-base text-gray-800 placeholder:text-gray-400 shadow-sm transition-colors duration-150 focus:outline-none focus:border-ifys-blue focus:ring-2 focus:ring-ifys-blue/30';

const IfysEnquiryForm = ({ formId, className = '' }) => {
  const [form, setForm] = useState(EMPTY_ENQUIRY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      await submitIfysEnquiry({ formValues: form });
      setSubmitted(true);
    } catch {
      setIsSubmitting(false);
      setError(
        'We could not submit your enquiry. Please try again, or contact us directly on 07 5438 3000 or admin@ifys.com.au.'
      );
    }
  };

  if (submitted) {
    return (
      <div className={className} role="status">
        <p className="text-center text-ifys-blue font-semibold py-8">
          Thank you — your enquiry has been received. We will be in touch shortly.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className={className} noValidate={false}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        <div>
          <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2" htmlFor={`${formId}-firstName`}>
            First name<span className="text-gray-500">*</span>
          </label>
          <input
            id={`${formId}-firstName`}
            name="firstName"
            value={form.firstName}
            onChange={onChange}
            className={inputBase}
            required
            disabled={isSubmitting}
            autoComplete="given-name"
          />
        </div>
        <div>
          <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2" htmlFor={`${formId}-lastName`}>
            Last name<span className="text-gray-500">*</span>
          </label>
          <input
            id={`${formId}-lastName`}
            name="lastName"
            value={form.lastName}
            onChange={onChange}
            className={inputBase}
            required
            disabled={isSubmitting}
            autoComplete="family-name"
          />
        </div>
      </div>

      <div className="mt-5 sm:mt-6">
        <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2" htmlFor={`${formId}-phone`}>
          Phone<span className="text-gray-500">*</span>
        </label>
        <input
          id={`${formId}-phone`}
          name="phone"
          value={form.phone}
          onChange={onChange}
          className={inputBase}
          placeholder="Your best contact number"
          required
          disabled={isSubmitting}
          autoComplete="tel"
        />
      </div>

      <div className="mt-5 sm:mt-6">
        <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2" htmlFor={`${formId}-email`}>
          Email address<span className="text-gray-500">*</span>
        </label>
        <input
          id={`${formId}-email`}
          name="email"
          type="email"
          value={form.email}
          onChange={onChange}
          className={inputBase}
          placeholder="yourname@example.com"
          required
          disabled={isSubmitting}
          autoComplete="email"
        />
      </div>

      <div className="mt-5 sm:mt-6">
        <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2" htmlFor={`${formId}-enquiry`}>
          Your enquiry: (if you would like us to respond, please provide your preferred method of contact)
        </label>
        <textarea
          id={`${formId}-enquiry`}
          name="enquiry"
          value={form.enquiry}
          onChange={onChange}
          className={inputBase}
          rows={4}
          placeholder="e.g. I'd like more information regarding…"
          disabled={isSubmitting}
        />
      </div>

      <div className="mt-5 sm:mt-6 flex items-start gap-3">
        <input
          id={`${formId}-newsletter`}
          name="newsletter"
          type="checkbox"
          checked={form.newsletter}
          onChange={onChange}
          className="mt-1 h-4 w-4 rounded border-gray-300 text-ifys-blue focus:ring-ifys-blue"
          disabled={isSubmitting}
        />
        <label htmlFor={`${formId}-newsletter`} className="text-sm text-gray-700">
          I agree to receive newsletters
        </label>
      </div>

      {error && (
        <p className="mt-6 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3" role="alert">
          {error}
        </p>
      )}

      <div className="mt-8 flex flex-col items-center">
        <Button
          variant="primary"
          size="md"
          type="submit"
          className="min-w-[200px]"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'SUBMITTING…' : 'SUBMIT ENQUIRY'}
        </Button>
        <p className="mt-3 text-xs text-gray-500">* Denotes required field</p>
      </div>
    </form>
  );
};

export default IfysEnquiryForm;
